import {getVideoDimentions} from '../../utils/getVideoDimentions';
import {getImageDimentions} from '../../utils/resizeImage';
import LibraryApi from '../api/LibraryAPI';
import {VideoGenerationConfig} from '../models/AI/VideoGenerationConfig';
import {GeneratedVideoChunk} from '../models/LibraryFile/GeneratedVideoChunk';
import {MEDIA_FILES_ALL} from '../models/LibraryFile/MediaFile';

class LibraryRepository {
  private libraryApi = new LibraryApi();

  public async uploadVideo(
    organizationId: string,
    file: File,
    duration: number,
    targetRes: number,
  ) {
    const urlResult = await this.libraryApi.generateVideoUploadLink(
      organizationId,
      file.type,
      Math.ceil(file.size / 1024 / 1024),
    );
    if (!urlResult.success || !urlResult.data) {
      return urlResult;
    }
    try {
      const uploadResult = await this.libraryApi.uploadFileToStorage(
        file,
        urlResult.data.uploadUrl,
      );
      if (uploadResult.ok) {
        if (file.type.includes('image')) {
          const dimentions = await getImageDimentions(file);
          return await this.libraryApi.createMediaLibraryFile(
            organizationId,
            urlResult.data.storageFileName,
            file.type,
            file.name,
            Math.ceil(file.size / 1024 / 1024),
            duration,
            dimentions?.width || 0,
            dimentions?.height || 0,
            null,
          );
        } else if (file.type.includes('video')) {
          const dimentions = await getVideoDimentions(file);
          return await this.libraryApi.createMediaLibraryFile(
            organizationId,
            urlResult.data.storageFileName,
            file.type,
            file.name,
            Math.ceil(file.size / 1024 / 1024),
            duration,
            dimentions?.width || 0,
            dimentions?.height || 0,
            targetRes,
          );
        } else {
          return await this.libraryApi.createMediaLibraryFile(
            organizationId,
            urlResult.data.storageFileName,
            file.type,
            file.name,
            Math.ceil(file.size / 1024 / 1024),
            duration,
            0,
            0,
            null,
          );
        }
      }
    } catch {
      return Promise.resolve({
        success: false,
        errors: ['unknown'],
      });
    }

    return urlResult;
  }

  public getMediaFiles(
    page: number,
    organizationId: string,
    pageSize: number,
    orderBy: string,
    typeFilter?: number,
    search?: string,
    orderAsk?: boolean,
    fileStatusFilter?: number,
  ) {
    return this.libraryApi.getLibraryFiles(
      page,
      organizationId,
      pageSize,
      orderBy,
      typeFilter !== undefined ? typeFilter : MEDIA_FILES_ALL,
      search,
      orderAsk,
      fileStatusFilter,
    );
  }

  public getByContentId(organizationId: string, contentId: string) {
    return this.libraryApi.getFileByContentId(organizationId, contentId);
  }

  public deleteMediaFile(organizationId: string, mediaFileId: string) {
    return this.libraryApi.deleteMediaFile(organizationId, mediaFileId);
  }

  public downloadTranscript(organizationId: string, mediaFileId: string) {
    return this.libraryApi.downloadTranscript(organizationId, mediaFileId);
  }

  public async getVimeoVideos(
    organizationId: string,
    page: number,
    pageSize: number,
  ) {
    return this.libraryApi.getVimeoVideos(organizationId, page, pageSize);
  }

  public async generateAiVideosFromSlides(
    organizationId: string,
    config: VideoGenerationConfig,
    assistantAvatar?: File,
  ) {
    const result = await this.libraryApi.generateAiVideosFromSlides(
      organizationId,
      config,
      assistantAvatar,
    );
    return result;
  }

  public async getFileOrientation(organizationId: string, fileId: string) {
    return this.libraryApi.getFileOrientation(organizationId, fileId);
  }

  public getVideoChunksForGeneratedContent(
    organizationId: string,
    mediaFileId: string,
  ) {
    return this.libraryApi.getVideoChunksForGeneratedContent(
      organizationId,
      mediaFileId,
    );
  }

  public async updateVideoChunksForGeneratedContent(
    organizationId: string,
    mediaFileId: string,
    chunks: GeneratedVideoChunk[],
  ) {
    return this.libraryApi.updateVideoChunksForGeneratedContent(
      organizationId,
      mediaFileId,
      chunks,
    );
  }

  public async compileVideoFile(organizationId: string, mediaFileId: string) {
    return this.libraryApi.compileVideoFile(organizationId, mediaFileId);
  }

  public async editChunk(
    organizationId: string,
    mediaFileId: string,
    option: EditChunkOption,
    chunkId: string,
    additionalParameters: Record<string, any> = {},
  ) {
    return this.libraryApi.editChunk(
      organizationId,
      mediaFileId,
      option,
      chunkId,
      additionalParameters,
    );
  }

  public async addChunk(
    organizationId: string,
    mediaFileId: string,
    payload: {
      libraryMediaFileId?: string;
      storyBlocksVideoId?: number;
      start: number;
      stop: number;
      mute: boolean;
    },
  ) {
    return this.libraryApi.addChunk(organizationId, mediaFileId, payload);
  }

  public async saveEdit(organizationId: string, mediaFileId: string) {
    return this.libraryApi.saveEdit(organizationId, mediaFileId);
  }
  public async cancelEdit(organizationId: string, mediaFileId: string) {
    return this.libraryApi.cancelEdit(organizationId, mediaFileId);
  }

  public async cancelGenerationAndDelete(
    organizationId: string,
    mediaFileId: string,
  ) {
    return this.libraryApi.cancelGenerationAndDelete(
      organizationId,
      mediaFileId,
    );
  }

  public async getStoryblocksMedia(
    organizationId: string,
    chunkId: string | null | undefined,
    page: number,
    pageSize: number,
    search: string,
  ) {
    return this.libraryApi.getStoryblocksMedia(
      organizationId,
      chunkId,
      page,
      pageSize,
      search,
    );
  }
  public async changeChunkToStoryblocksMedia(
    organizationId: string,
    mediaFileId: string,
    chunkId: string,
    storyBlocksVideoId: string,
  ) {
    return this.editChunk(
      organizationId,
      mediaFileId,
      EditChunkOption.ChangeMedia,
      chunkId,
      {storyBlocksVideoId: storyBlocksVideoId},
    );
  }
  public async changeChunkToLibraryMedia(
    organizationId: string,
    mediaFileId: string,
    chunkId: string, // AiVideoChunkId
    libraryMediaId: string, // LibraryMediaId
    isImage: boolean, // true -> Option 6, false -> Option 5
  ) {
    // ⚠️ Importante: NO usar editChunk genérico aquí (usa minúsculas).
    // Llamamos al método específico del API que respeta el casing exacto.
    return this.libraryApi.replaceChunkWithLibraryMedia(
      organizationId,
      mediaFileId,
      chunkId,
      libraryMediaId,
      isImage,
    );
  }

  public async getChunkNarration(
    organizationId: string,
    mediaFileId: string,
    chunkId: string,
  ) {
    return this.libraryApi.getChunkNarration(
      organizationId,
      mediaFileId,
      chunkId,
    );
  }

  public async updateChunkNarration(
    organizationId: string,
    mediaFileId: string,
    chunkId: string,
    text: string,
  ) {
    return this.libraryApi.updateChunkNarration(
      organizationId,
      mediaFileId,
      chunkId,
      text,
    );
  }
  public async replaceAIMedia(
    organizationId: string,
    mediaFileId: string,
    chunkId: string,
    replacements: {artifactUri: string; libraryMediaFileId: string}[],
  ) {
    return this.libraryApi.replaceAIMedia(
      organizationId,
      mediaFileId,
      chunkId,
      replacements,
    );
  }
}

export enum EditChunkOption {
  MuteAudio = 0,
  Regenerate = 1,
  Delete = 2,
  ChangeMedia = 3,
  Reorder = 4,
  ChangeFromLibrary = 5,
  // ChangeFromLibraryImage = 6 on backend
  EditNarration = 7,
  EditScript = -1, // not implemented on backend yet
  ReplaceAIMedia = 8,
  ChangeBackgroundAudio = 9,
  DisableBackgroundAudio = 10,
}
export default LibraryRepository;
